import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const VendorProducts = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: productsData, isLoading } = useQuery(
    ['vendor-products', statusFilter],
    () => api.get('/products/vendor/my-products', {
      params: { status: statusFilter }
    }).then(res => res.data.data)
  );

  const toggleStatusMutation = useMutation(
    (product) => {
      const newStatus = product.status === 'active' ? 'draft' : 'active';
      return api.put(`/products/${product.id}`, { status: newStatus });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vendor-products');
        toast.success('تم تحديث حالة المنتج');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  const deleteMutation = useMutation(
    (productId) => api.delete(`/products/${productId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vendor-products');
        toast.success('تم حذف المنتج');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  if (isLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">منتجاتي</h1>
          <Link
            to="/vendor/products/add"
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="w-5 h-5 ml-2" />
            إضافة منتج جديد
          </Link>
        </div>

        {/* Filter */}
        <div className="bg-white p-4 rounded-lg shadow">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">جميع المنتجات</option>
            <option value="active">نشط</option>
            <option value="inactive">معطل</option>
          </select>
        </div>

        {/* Products Grid */}
        {productsData?.products?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productsData.products.map((product) => (
              <div key={product.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="relative h-48 bg-gray-200">
                  {product.images?.[0]?.url ? (
                    <img
                      src={product.images[0].url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      لا توجد صورة
                    </div>
                  )}
                  {product.status !== 'active' && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="text-white font-bold">معطل</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-bold text-primary-600">
                      {product.price} جنيه
                    </span>
                    <span className="text-sm text-gray-500">
                      المخزون: {product.quantity}
                    </span>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <div className="flex space-x-2 space-x-reverse">
                      <Link
                        to={`/vendor/products/edit/${product.id}`}
                        className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-center"
                      >
                        <PencilIcon className="w-4 h-4 inline ml-1" />
                        تعديل
                      </Link>
                      <button
                        onClick={() => toggleStatusMutation.mutate(product)}
                        className={`px-3 py-2 text-sm rounded-md ${
                          product.status === 'active'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {product.status === 'active' ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
                            deleteMutation.mutate(product.id);
                          }
                        }}
                        className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                    <Link
                      to={`/vendor/products/${product.id}/inventory`}
                      className="w-full px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 text-center"
                    >
                      <ClipboardDocumentListIcon className="w-4 h-4 inline ml-1" />
                      سجل المخزون
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600 mb-4">لا توجد منتجات بعد</p>
            <Link
              to="/vendor/products/add"
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="w-5 h-5 ml-2" />
              إضافة أول منتج
            </Link>
          </div>
        )}
      </div>
    </VendorLayout>
  );
};

export default VendorProducts;